import { useContext, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { AppContext } from "../../AppContext";
import { brandTemplatesVersionState, memberState } from "../../state/atoms";
import { env } from "../../env/env";
import BrandTemplateApi, { BrandTemplateDto } from "../../api/BrandTemplateApi";
import BrandKitApi from "../../api/BrandKitApi";
import RendererThumb from "../common/RendererThumb";
import "./BrandTemplateCarousel.scss";
import { BrandColorDto, BrandLogoDto } from "../../generated/dashboard-client";
import { getSizeLabel, getSizeDimensions } from "../../util/utils";
import { useBrandDestination } from "../../service/useBrandDestination";

const tiny = require("tinycolor2");

enum Direction {
  Left,
  Right,
}

const ScrollStep = 500;

export interface BrandTemplateCarouselProps {
  onLoaded?: (hasTemplates: boolean) => void;
}

export default function BrandTemplateCarousel({ onLoaded }: BrandTemplateCarouselProps) {
  const { location } = useContext(AppContext);
  const member = useRecoilValue(memberState);
  const brandTemplatesVersion = useRecoilValue(brandTemplatesVersionState);
  const {
    destinationFor: brandDestinationFor,
    goToBrand,
    ensureKits: ensureBrandKits,
  } = useBrandDestination();

  const [templates, setTemplates] = useState<BrandTemplateDto[]>([]);
  const [loaded, setLoaded] = useState(false);

  const carousel = useRef<HTMLUListElement>(null);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(true);

  const [brandColor, setBrandColor] = useState<BrandColorDto>();
  const [brandLogo, setBrandLogo] = useState<BrandLogoDto>();

  const isAdminOrOwner = member.owner || member.role === "Admin";

  useEffect(() => {
    if (!location?.internalId) return;
    let cancelled = false;
    setLoaded(false);
    BrandTemplateApi.listByLocation(location.internalId)
      .then((data) => {
        if (cancelled) return;
        const list = data || [];
        setTemplates(list);
        onLoaded?.(list.length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setTemplates([]);
        onLoaded?.(false);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.internalId, brandTemplatesVersion]);

  useEffect(() => {
    if (!location?.internalId || !member.teamId) return;
    let cancelled = false;
    BrandKitApi.getPreview(location.internalId)
      .then((res) => {
        if (cancelled) return;
        setBrandColor(res.data.color);
        setBrandLogo(res.data.logo);
      })
      .catch(() => {
        if (cancelled) return;
        setBrandColor(undefined);
        setBrandLogo(undefined);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.internalId, member.teamId]);

  useEffect(() => {
    const current = carousel.current;
    if (!current) return;

    const update = () => {
      const width = current.clientWidth;
      const fullWidth = current.scrollWidth;
      const scrollLeft = current.scrollLeft;
      setLeft(scrollLeft > 0);
      setRight(width + scrollLeft + 10 < fullWidth);
    };

    update();
    current.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      current.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [templates]);

  function scroll(dir: Direction) {
    const current = carousel.current;
    if (!current) return;
    current.scroll({
      top: 0,
      left:
        dir === Direction.Left
          ? current.scrollLeft - ScrollStep
          : current.scrollLeft + ScrollStep,
      behavior: "smooth",
    });
  }

  useEffect(() => {
    if (isAdminOrOwner) ensureBrandKits();
  }, [isAdminOrOwner, ensureBrandKits]);

  function openTemplate(template: BrandTemplateDto) {
    if (!template.id) return;
    window.location.href =
      env.timberContextPath +
      "/design?parentId=" +
      encodeURIComponent(template.id) +
      "&fromSample=true";
  }

  if (!loaded || templates.length === 0) return null;

  return (
    <section className="brand-template-carousel">
      <header className="brand-template-carousel-header">
        {brandLogo && brandLogo.path &&
        <img className="brand-template-carousel-header-logo" src={`${env.s3Path}/${brandLogo.path}`} alt={`${location.name} logo`} />}
        <h3>{location.name} Brand Templates</h3>
        {isAdminOrOwner && (
          <a
            className="manage-brand-link"
            href={brandDestinationFor(location)}
            onClick={(e) => {
              e.preventDefault();
              goToBrand(location);
            }}
          >
            Manage Brand
          </a>
        )}
      </header>
      <div className="brand-template-carousel-body">
        {left && (
          <Button className={"bt btn-left"} onClick={() => scroll(Direction.Left)}>
            <LeftOutlined />
          </Button>
        )}
        <ul ref={carousel}>
          {templates.map((template, idx) => (
            <li key={template.id || idx} className="brand-template-card">
              <span
                className="brand-template-thumb"
                onClick={() => openTemplate(template)}
                style={{backgroundColor: brandColor?.colorCode ? tiny(brandColor?.colorCode).setAlpha(0.1).toRgbString() : "#efefef"}}
              >
                <RendererThumb
                  id={template.id}
                  alt={template.name}
                  height={225}
                  loading="lazy"
                  className="brand-template-thumb-img"
                  fallback={
                    <span className="brand-template-thumb-fallback">
                      {template.name}
                    </span>
                  }
                />
                <span className="brand-template-use">Use this</span>
              </span>
              <div className="brand-template-meta">
                {(() => {
                  const displayName = template.name || `Menu ${idx + 1}`;
                  const sizeLabel = template.productType === 'custom' ? 'Custom Size' : getSizeLabel(template.productType);
                  const sizeDims = template.productType === 'custom' ? `${template.width} x ${template.height}${template.sizeUnit?.toLowerCase()}` : getSizeDimensions(template.productType);
                  return (
                    <>
                      <p
                        className="brand-template-name"
                        title={displayName}
                      >
                        {displayName}
                      </p>
                      {sizeLabel && <span className="brand-template-size"><span className="size-label">{sizeLabel}</span> • {sizeDims}</span>}
                    </>
                  );
                })()}
              </div>
            </li>
          ))}
        </ul>
        {right && (
          <Button className={"bt btn-right"} onClick={() => scroll(Direction.Right)}>
            <RightOutlined />
          </Button>
        )}
      </div>
    </section>
  );
}
